import { Filter } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import type { AssistantContextSummaryState } from '../types';
import styles from '../styles/AIAssistant.module.css';
import { Badge, Group, Skeleton, Stack, Text } from '../ui/primitives.js';
import { getAssistantMotionTransition } from './internal/assistantMotion';

export type AIAssistantContextSummaryProps = {
  summary?: AssistantContextSummaryState;
  isLoading?: boolean;
};

export function AIAssistantContextSummary({
  summary,
  isLoading,
}: AIAssistantContextSummaryProps) {
  const reducedMotion = Boolean(useReducedMotion());
  const effectiveSummary = summary ?? {};
  const loading = isLoading ?? effectiveSummary.isLoading;
  const filters = effectiveSummary.filters ?? [];
  const hasFilters = filters.length > 0;
  const hasCount = typeof effectiveSummary.count === 'number' && effectiveSummary.count > 0;
  const hideWhenEmpty = effectiveSummary.hideWhenEmpty ?? true;

  if (loading) {
    return (
      <div className={styles.contextHeader} data-testid="ai-context-header">
        <Skeleton height={16} width="60%" />
      </div>
    );
  }

  if (hideWhenEmpty && !hasFilters && !hasCount) {
    return null;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={JSON.stringify({
          count: effectiveSummary.count,
          label: effectiveSummary.label,
          searchLabel: effectiveSummary.searchLabel,
          filters,
        })}
        className={styles.contextHeader}
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
        animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
        transition={getAssistantMotionTransition('micro', reducedMotion)}
        data-testid="ai-context-header"
      >
        <Stack gap={4}>
          {hasCount ? (
            <Text size="xs" c="dimmed">
              Analyzing <Text component="span" fw={600}>{effectiveSummary.count}</Text> {effectiveSummary.label ?? 'records'}
              {effectiveSummary.searchLabel ? (
                <Text component="span"> for &quot;{effectiveSummary.searchLabel}&quot;</Text>
              ) : null}
            </Text>
          ) : null}

          {hasFilters ? (
            <Group gap={4}>
              <Filter size={12} className={styles.filterIcon} />
              {filters.map((filter) => (
                <Badge
                  key={filter.label}
                  size="xs"
                  variant={filter.variant ?? 'light'}
                  color={filter.color ?? 'gray'}
                >
                  {filter.label}
                </Badge>
              ))}
            </Group>
          ) : null}
        </Stack>
      </motion.div>
    </AnimatePresence>
  );
}
